import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2, Copy, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { toE164India, toErrorMessage } from "../../services/crmApi";
import {
  createServiceLead,
  createServiceLeadDraft,
  verifyServiceLeadOtp,
  resendServiceLeadOtp,
  BODY_CLASS_TO_API,
  FUEL_TO_API,
} from "../../services/publicCrmApi";
import {
  trackEvent, trackConversion, setEnhancedConversion, captureUtm, getAttribution,
  trackFormStart, trackFieldAbandon, trackOtpSent, trackOtpSendFailed, trackOtpVerified,
} from "../../utils/gtag";
import { detectFakeMobile, isLikelyRealIndianMobile } from "../../utils/mobileValidation";
import { buildCarServiceWhatsAppHref } from "./whatsappConfig";
import { useCarServicePackage } from "./CarServicePackageContext";
import { useCarServiceCity } from "./CarServiceCityContext";

const FORM_ID = "lead_form_carservice";

/** Tiny cookie reader — only used as a wd_session_id fallback. */
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Maps the lookup-widget vehicle shape to the CRM /service-leads
 * `vehicle` body. Tolerates both make/model flow (brand_id/model_id) and
 * by-RC flow (registration_number + brand/model from VAHAN).
 */
function buildVehiclePayload(vehicle) {
  if (!vehicle) return undefined;
  const fuelKey = String(vehicle.fuel || vehicle.fuel_type || "petrol").toLowerCase();
  const bodyKey = String(vehicle.body_class || "hatchback").toLowerCase();
  const titleCase = (s) =>
    String(s || "")
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ")
      .trim();
  // Brand / model: prefer the human-readable VAHAN string if available,
  // else best-effort title-case the slug from the make/model flow.
  const brand = vehicle.brand || vehicle.maker || titleCase((vehicle.brand_id || "").replace(/-/g, " "));
  const model = vehicle.model || titleCase((vehicle.model_id || "").replace(/-/g, " "));
  const payload = {
    rc_number: vehicle.registration_number || undefined,
    brand: brand || undefined,
    model: model || undefined,
    fuel_type: FUEL_TO_API[fuelKey] || titleCase(fuelKey),
    body_class: BODY_CLASS_TO_API[bodyKey] || titleCase(bodyKey.replace(/_/g, " ")),
    year: vehicle.year || vehicle.manufacture_year || undefined,
  };
  // Strip undefined keys for a clean payload
  Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
  return payload;
}

/**
 * Lead-capture modal — minimal 2-step flow on a single screen:
 *
 *   Step 1: Name + Mobile  → "Continue · Verify Mobile"
 *           ↳ POST /service-leads  →  CRM SMSes OTP
 *   Step 2: 6-digit OTP    → "Confirm · Call Me Back"
 *           ↳ POST /service-leads/{id}/verify-otp
 *   Step 3: Success — "We'll call you back in 30 mins"
 *
 * No payment, no slot picker. Pure paid-ads lead-gen.
 */
const CarServiceBookingModal = ({ open, onClose, onLeadSuccess, price, vehicle }) => {
  const { pkg } = useCarServicePackage();
  const { selectedCity } = useCarServiceCity();
  const [step, setStep] = useState("contact"); // contact | otp | submitting | success | error
  const [form, setForm] = useState({ name: "", phone: "" });
  const [otpState, setOtpState] = useState({ leadId: null, otp: "", cooldown: 0 });
  const [busy, setBusy] = useState(false);
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);
  const cooldownTimer = useRef(null);

  // Draft-lead state — invisible to the user; captured the moment they
  // type a complete 10-digit mobile so abandoners still show up in the CRM
  // as DRAFT_UNVERIFIED rows.
  const draftLeadIdRef = useRef(null);
  const draftDebounceRef = useRef(null);
  const lastDraftedMobileRef = useRef(null);
  // Fires book_now_clicked at most once per modal session — Smart Bidding
  // needs clean signal counts; spam-clicking the CTA must not inflate it.
  const bookNowFiredRef = useRef(false);
  const [draftSavedFlash, setDraftSavedFlash] = useState(false);

  // ---- form_start + field_abandon plumbing -------------------------------
  // Track first-focus per field within this modal session; on blur with
  // empty value, fire field_abandon (debounced 500ms — re-focus within
  // that window is treated as continuous editing, not abandonment).
  const fieldFocusedRef = useRef({});       // fieldName -> true
  const abandonTimersRef = useRef({});      // fieldName -> timeoutId

  const handleFieldFocus = (fieldName) => () => {
    fieldFocusedRef.current[fieldName] = true;
    // Cancel pending abandon — user came back within debounce window.
    if (abandonTimersRef.current[fieldName]) {
      clearTimeout(abandonTimersRef.current[fieldName]);
      delete abandonTimersRef.current[fieldName];
    }
    trackFormStart({ formId: FORM_ID, first_field: fieldName, firstField: fieldName });
  };

  const handleFieldBlur = (fieldName, getValue) => () => {
    if (!fieldFocusedRef.current[fieldName]) return;
    const v = (getValue() || "").toString().trim();
    if (v) return;
    // Debounce 500ms — if re-focused within that window, focus handler
    // cancels the timeout above. Otherwise, fire abandon and clear flag
    // so the next focus → blur cycle can re-fire it.
    abandonTimersRef.current[fieldName] = setTimeout(() => {
      trackFieldAbandon({ formId: FORM_ID, fieldName });
      fieldFocusedRef.current[fieldName] = false;
      delete abandonTimersRef.current[fieldName];
    }, 500);
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep("contact");
      setError(null);
      setLead(null);
      setForm({ name: "", phone: "" });
      setOtpState({ leadId: null, otp: "", cooldown: 0 });
      draftLeadIdRef.current = null;
      lastDraftedMobileRef.current = null;
      bookNowFiredRef.current = false;
      fieldFocusedRef.current = {};
      Object.values(abandonTimersRef.current).forEach(clearTimeout);
      abandonTimersRef.current = {};
      setDraftSavedFlash(false);
    }
    return () => {
      clearInterval(cooldownTimer.current);
      clearTimeout(draftDebounceRef.current);
    };
  }, [open]);

  // Cooldown tick
  useEffect(() => {
    if (otpState.cooldown > 0) {
      cooldownTimer.current = setTimeout(
        () => setOtpState((s) => ({ ...s, cooldown: s.cooldown - 1 })),
        1000,
      );
    }
    return () => clearTimeout(cooldownTimer.current);
  }, [otpState.cooldown]);

  // ---- Draft-lead auto-capture --------------------------------------
  // Fire /service-leads/draft as soon as the user types a 10-digit mobile,
  // debounced 1.5s per W1 spec (the CRM team's funnel-analytics work tunes
  // their dedup window assuming a ~1.5s settle time). Fire-and-forget — never
  // blocks UI, never surfaces errors. Server is idempotent on (mobile, last
  // 30 min) — same lead_id returned on dupe.
  useEffect(() => {
    clearTimeout(draftDebounceRef.current);
    if (!open) return;
    if (step !== "contact") return;
    const phone10 = (form.phone || "").replace(/\D/g, "");
    if (phone10.length !== 10 || !/^[6-9]/.test(phone10)) return;
    // Skip API call for sequence / repeated-digit fakes (9876543210,
    // 9999999999, etc). Saves SMS budget + keeps CRM funnel clean.
    if (!isLikelyRealIndianMobile(phone10)) return;
    if (phone10 === lastDraftedMobileRef.current) return;

    draftDebounceRef.current = setTimeout(async () => {
      try {
        const utm = captureUtm() || {};
        const attr = getAttribution();
        // wd_session_id — set by the CRM tracker (v1.js). Fall back to the
        // legacy cookie name in case the tracker hasn't loaded yet.
        const wdSessionId = (typeof window !== "undefined")
          ? (window.__wd_session_id
              || window.__wd_track?.getSessionId?.()
              || readCookie("wd_session_id")
              || readCookie("wd_session")
              || undefined)
          : undefined;
        const res = await createServiceLeadDraft({
          mobile: phone10,
          package_id: pkg?.id,
          city: selectedCity?.name || undefined,
          vehicle: buildVehiclePayload(vehicle),
          utm_source: utm.utm_source || "carservice_lp",
          utm_campaign: utm.utm_campaign || undefined,
          // Full Google Ads attribution — CRM persists under source_meta.*
          gclid: attr.gclid || undefined,
          gbraid: attr.gbraid || undefined,
          wbraid: attr.wbraid || undefined,
          gad_source: attr.gad_source || undefined,
          utm_medium: attr.utm_medium || undefined,
          utm_term: attr.utm_term || undefined,
          utm_content: attr.utm_content || undefined,
          referrer: attr.referrer || undefined,
          landing_page: attr.landing_page || undefined,
          // CRM funnel analytics — stitches the draft to the tracker's
          // server-side session so DRAFT_UNVERIFIED → NEW transitions
          // attribute correctly. W1 spec requirement.
          wd_session_id: wdSessionId,
        });
        if (res?.lead_id) {
          draftLeadIdRef.current = res.lead_id;
          lastDraftedMobileRef.current = phone10;
          // 200ms cosmetic flash next to the mobile field
          setDraftSavedFlash(true);
          setTimeout(() => setDraftSavedFlash(false), 1200);
          trackEvent("draft_lead_captured", { lead_id: res.lead_id });
        }
      } catch (_) {
        // Silent — draft is fire-and-forget. The submit step will fall
        // back to a fresh insert if draft_lead_id is null.
      }
    }, 1500);

    return () => clearTimeout(draftDebounceRef.current);
    // Draft debounce is intentionally not keyed on selectedCity — the city
    // captured on draft is the one selected at the time the user typed
    // their mobile; changing city later doesn't need a re-draft (the final
    // /service-leads submit picks up the fresh city from context).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, form.phone, pkg?.id, vehicle]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validateContact = () => {
    if (!form.name.trim() || form.name.trim().length < 2) return "Enter your full name";
    const phone = toE164India(form.phone);
    if (!/^\+91[6-9]\d{9}$/.test(phone)) return "Enter a valid 10-digit Indian mobile number";
    // Block obvious fake / placeholder numbers (9876543210, 9999999999, etc).
    const fakeReason = detectFakeMobile(form.phone);
    if (fakeReason) return fakeReason;
    return null;
  };

  // --- Step 1 → Create lead (server triggers OTP SMS) -----------------
  const handleSendOtp = async () => {
    const err = validateContact();
    if (err) { toast.error(err); return; }
    // City is mandatory now — we're live in multiple cities so we MUST
    // capture where the customer is. Refuse to submit until they pick one
    // so CRM never receives a hardcoded default (was "Bengaluru" for all).
    if (!selectedCity?.name) {
      toast.error("Please select your city (top-left) before booking.");
      trackEvent("city_prompt_blocked_submit", { field: "city" });
      return;
    }
    // Secondary / soft conversion — fires once per modal session, gated
    // behind valid name+mobile so we never count invalid clicks. Marketing
    // must mark this as "Secondary" in Google Ads (not Primary) so Smart
    // Bidding optimizes for verified leads, not button-mashing.
    if (!bookNowFiredRef.current) {
      bookNowFiredRef.current = true;
      trackConversion("book_now_clicked", {
        value: price?.amount_display || pkg?.quoted_price || undefined,
        transaction_id: draftLeadIdRef.current || undefined,
      });
    }
    setBusy(true);
    try {
      // Strip +91 to 10-digit (CRM normalises but be explicit)
      const mobile10 = toE164India(form.phone).replace("+91", "");
      const utm = captureUtm() || {};
      const attr = getAttribution();
      // Prefer the CRM tracker's draft lead_id (v1.1.0+) — it stitches the
      // pre-OTP mobile capture to this main lead with the cleanest fidelity.
      // Falls back to our own local draft ref so we keep working both before
      // the tracker ships (currently 404) AND if the tracker fails to load.
      const trackerLeadId =
        (typeof window !== "undefined" && window.__wd_track && typeof window.__wd_track.getLeadId === "function")
          ? window.__wd_track.getLeadId(mobile10)
          : null;
      const draftLeadId = trackerLeadId || draftLeadIdRef.current || undefined;
      // Same wd_session_id we sent on the draft — keeps the funnel stitch
      // intact through DRAFT_UNVERIFIED → NEW → CONTACTED transitions.
      const wdSessionId = (typeof window !== "undefined")
        ? (window.__wd_session_id
            || window.__wd_track?.getSessionId?.()
            || readCookie("wd_session_id")
            || readCookie("wd_session")
            || undefined)
        : undefined;

      const leadPayload = {
        package_id: pkg?.id,
        customer_name: form.name.trim(),
        mobile: mobile10,
        city: selectedCity?.name,
        vehicle: buildVehiclePayload(vehicle),
        preferred_slot: "tomorrow_morning",
        utm_source: utm.utm_source || "carservice_lp",
        utm_campaign: utm.utm_campaign || undefined,
        // Full Google Ads attribution — CRM persists under source_meta.*
        gclid: attr.gclid || undefined,
        gbraid: attr.gbraid || undefined,
        wbraid: attr.wbraid || undefined,
        gad_source: attr.gad_source || undefined,
        utm_medium: attr.utm_medium || undefined,
        utm_term: attr.utm_term || undefined,
        utm_content: attr.utm_content || undefined,
        referrer: attr.referrer || undefined,
        landing_page: attr.landing_page || undefined,
        // Stitches the NEW lead to the same wd_session as the draft —
        // CRM funnel analytics joins on this for DRAFT→NEW attribution.
        wd_session_id: wdSessionId,
        // Upgrade the abandoner-draft in-place when present — server returns
        // the same lead_id (no duplicate doc). Tracker wins over local draft.
        draft_lead_id: draftLeadId,
      };
      const res = await createServiceLead(leadPayload);
      // GA4 funnel: backend 2xx → OTP dispatch confirmed
      trackOtpSent({ formId: FORM_ID });
      setOtpState({
        leadId: res.lead_id,
        otp: "",
        cooldown: res.otp_resend_in_seconds || 60,
      });
      setLead(res);
      setStep("otp");
      trackEvent("otp_sent", { purpose: "service_lead" });
    } catch (e) {
      const code = e?.response?.data?.detail?.error
        || e?.response?.data?.code
        || (e?.response?.status ? `http_${e.response.status}` : (e?.code || "unknown"));
      trackOtpSendFailed({ formId: FORM_ID, errorType: code });
      toast.error(toErrorMessage(e, "Couldn't send OTP. Try again in a moment."));
    } finally {
      setBusy(false);
    }
  };

  // --- Step 2 → Verify OTP (lead becomes NEW server-side) -------------
  const handleVerifyAndSubmit = async () => {
    if (!/^\d{6}$/.test(otpState.otp)) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setBusy(true);
    try {
      setStep("submitting");
      const result = await verifyServiceLeadOtp(otpState.leadId, otpState.otp, {
        city: selectedCity?.name || undefined,
      });
      trackOtpVerified({ formId: FORM_ID });

      const phoneE164 = toE164India(form.phone);
      setEnhancedConversion({ phone: phoneE164 });

      const enriched = {
        ...lead,
        ...result,
        lead_id: otpState.leadId,
        amount_display: lead?.quoted_price ?? price?.amount_display,
        booking_reference: otpState.leadId,
      };
      setLead(enriched);
      setStep("success");

      trackEvent("lead_submitted", {
        lead_id: otpState.leadId,
        value: enriched.amount_display,
      });
      // Google Ads conversion: flat `value: 1.0` for the LEAD signal so it
      // counts as 1 lead in reports (not double-counting revenue with the
      // eventual `purchase` event). transaction_id ensures dedupe if the
      // success screen is refreshed.
      trackConversion("lead_submitted", {
        value: 1.0,
        transaction_id: otpState.leadId,
      });
      onLeadSuccess?.(enriched);
    } catch (e) {
      setError(toErrorMessage(e, "Couldn't verify your code. Try again or WhatsApp us."));
      setStep("error");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (otpState.cooldown > 0 || !otpState.leadId) return;
    try {
      const r = await resendServiceLeadOtp(otpState.leadId);
      setOtpState((s) => ({ ...s, cooldown: r?.resend_in_seconds || 60 }));
      toast.success("OTP sent again");
    } catch (e) {
      toast.error(toErrorMessage(e, "Couldn't resend. Try again."));
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
        data-testid="booking-modal-overlay"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[460px] bg-white rounded-t-3xl sm:rounded-2xl shadow-xl max-h-[92vh] flex flex-col"
          data-testid="booking-modal"
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-blue-600">
                {step === "success" ? "All set" : "Get your instant quote"}
              </div>
              <div className="text-base font-extrabold text-slate-900 mt-1 leading-tight">
                {vehicle?.display || "Your car"}
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="booking-modal-close"
              className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {(step === "contact" || step === "otp") && (
              <ContactStep
                form={form}
                update={update}
                otpSent={step === "otp"}
                phone={form.phone}
                otp={otpState.otp}
                cooldown={otpState.cooldown}
                setOtp={(v) => setOtpState((s) => ({ ...s, otp: v }))}
                onResend={handleResend}
                draftSavedFlash={draftSavedFlash}
                onFieldFocus={handleFieldFocus}
                onFieldBlur={handleFieldBlur}
              />
            )}

            {step === "submitting" && (
              <div className="py-14 flex flex-col items-center justify-center text-center">
                <Loader2 className="h-9 w-9 text-blue-600 animate-spin mb-4" />
                <div className="text-base font-extrabold text-slate-900 mb-1">Saving your details</div>
                <div className="text-sm text-slate-500">This takes a few seconds</div>
              </div>
            )}

            {step === "success" && lead && <SuccessStep lead={lead} price={price} vehicle={vehicle} />}

            {step === "error" && (
              <div className="py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 inline-flex items-center justify-center mb-4">
                  <X className="h-5 w-5 text-red-600" />
                </div>
                <div className="text-lg font-extrabold text-slate-900 mb-2">
                  Couldn't submit your request
                </div>
                <div className="text-sm text-slate-600 max-w-sm mx-auto mb-5">{error}</div>
                <a
                  href={buildCarServiceWhatsAppHref({
                    message: `Hi, I'd like a callback for general service on my ${vehicle?.display || "car"} (₹${price?.amount_display}).`,
                    city: selectedCity,
                    clickType: "modal-error",
                  })}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-extrabold text-sm transition-all"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp us instead
                </a>
              </div>
            )}
          </div>

          {/* Footer action — single primary button that morphs based on step */}
          {step === "contact" && (
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-3xl sm:rounded-b-2xl">
              <button
                onClick={handleSendOtp}
                disabled={busy || !isLikelyRealIndianMobile(form.phone)}
                data-testid="booking-send-otp-btn"
                data-cta-label="Continue · Verify Mobile"
                data-cta-location="modal"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-extrabold text-base tracking-tight transition-all shadow-[0_8px_24px_-6px_rgba(37,99,235,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(37,99,235,0.6)]"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Continue · Verify Mobile
              </button>
              <p className="text-[11px] text-center text-slate-500 mt-2.5">
                We'll send a 6-digit OTP to verify your number
              </p>
            </div>
          )}
          {step === "otp" && (
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-3xl sm:rounded-b-2xl">
              <button
                onClick={handleVerifyAndSubmit}
                disabled={busy || otpState.otp.length !== 6}
                data-testid="booking-verify-btn"
                data-cta-label="Confirm · Call Me Back"
                data-cta-location="modal"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-extrabold text-base tracking-tight transition-all shadow-[0_8px_24px_-6px_rgba(37,99,235,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(37,99,235,0.6)]"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
                Confirm · Call Me Back
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// --- Sub-components ----------------------------------------------------

const FormField = ({ label, sublabel, children }) => (
  <label className="block">
    <span className="block text-[11px] font-extrabold uppercase tracking-[0.16em] text-slate-500 mb-1.5">
      {label}
      {sublabel && <span className="ml-2 text-slate-400 font-bold lowercase tracking-normal">{sublabel}</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full px-4 py-3.5 bg-white border border-slate-200 rounded-xl outline-none transition-all focus:border-slate-900 focus:ring-2 focus:ring-blue-600/20 text-slate-900 text-base placeholder:text-slate-400";

const ContactStep = ({
  form, update,
  otpSent = false,
  phone, otp, cooldown, setOtp, onResend,
  draftSavedFlash = false,
  onFieldFocus, onFieldBlur,
}) => {
  // Live fake-number detection — only surfaces the error once the user has
  // typed all 10 digits, so we don't yell at them mid-entry. Returns null
  // for empty / partial input.
  const phone10 = (form.phone || "").replace(/\D/g, "");
  const mobileError = phone10.length === 10 ? detectFakeMobile(phone10) : null;

  return (
  <div className="space-y-4">
    {!otpSent && (
      <p className="text-sm text-slate-500 leading-relaxed mb-1">
        Share a few quick details and we'll show your custom service price.
        Our team will call you back within 30 minutes to confirm.
      </p>
    )}

    <FormField label="Full Name">
      <input
        type="text" value={form.name} onChange={update("name")}
        onFocus={onFieldFocus?.("name")}
        onBlur={onFieldBlur?.("name", () => form.name)}
        placeholder="e.g., Rohan Mehta"
        autoComplete="name"
        readOnly={otpSent}
        className={`${inputCls} ${otpSent ? "bg-slate-50 text-slate-700" : ""}`}
        data-testid="booking-name-input"
      />
    </FormField>

    <FormField
      label="Mobile Number"
      sublabel={
        draftSavedFlash ? (
          <span
            className="inline-flex items-center gap-1 text-green-600 font-semibold animate-in fade-in duration-200"
            data-testid="booking-draft-saved"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-600" />
            </span>
            Saved
          </span>
        ) : (
          "India only · +91"
        )
      }
    >
      <div className="flex items-stretch gap-2">
        <div className="px-4 py-3.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 font-mono font-bold text-base flex items-center">
          +91
        </div>
        <input
          type="tel" inputMode="numeric"
          value={form.phone}
          onChange={(e) => update("phone")({ ...e, target: { ...e.target, value: e.target.value.replace(/\D/g, "").slice(0, 10) } })}
          onFocus={onFieldFocus?.("mobile")}
          onBlur={onFieldBlur?.("mobile", () => form.phone)}
          placeholder="98XXXXXXXX"
          maxLength={10}
          autoComplete="tel-national"
          readOnly={otpSent}
          aria-invalid={!!mobileError}
          aria-describedby={mobileError ? "mobile-error" : undefined}
          className={`${inputCls} font-mono tracking-wider ${otpSent ? "bg-slate-50 text-slate-700" : ""} ${mobileError ? "border-red-400 focus:border-red-500 focus:ring-red-500/30" : ""}`}
          data-testid="booking-phone-input"
        />
      </div>
      {mobileError && !otpSent && (
        <p
          id="mobile-error"
          role="alert"
          data-testid="booking-phone-error"
          className="mt-1.5 text-xs font-semibold text-red-600 flex items-start gap-1.5"
        >
          <X className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
          <span>{mobileError}</span>
        </p>
      )}
    </FormField>

    {/* Inline OTP — same modal, progressive disclosure. Slides in once OTP is sent. */}
    {otpSent && (
      <div
        className="pt-2 border-t border-dashed border-slate-200 animate-in fade-in slide-in-from-bottom-2 duration-300"
        data-testid="booking-otp-block"
      >
        <FormField label="Verification Code" sublabel="6-digit OTP">
          <input
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onFocus={onFieldFocus?.("otp")}
            onBlur={onFieldBlur?.("otp", () => otp)}
            maxLength={6}
            placeholder="000000"
            autoFocus
            data-testid="otp-input-field"
            className={`${inputCls} font-mono text-2xl tracking-[0.4em] font-bold text-center`}
          />
        </FormField>
        <div className="flex items-center justify-between mt-2.5 text-[11px]">
          <span className="text-slate-500">
            OTP sent to <span className="font-mono font-bold text-slate-700">+91 {phone}</span>
          </span>
          <button
            type="button"
            onClick={onResend}
            disabled={cooldown > 0}
            className="text-blue-600 font-bold hover:underline disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
          </button>
        </div>
      </div>
    )}
  </div>
  );
};

const SuccessStep = ({ lead, price, vehicle }) => {
  const [copied, setCopied] = useState(false);
  const ref = lead?.booking_reference || "WD-SVC-PENDING";
  const copy = () => {
    navigator.clipboard?.writeText(ref);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="py-2 text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-16 h-16 rounded-full bg-green-50 border border-green-200 inline-flex items-center justify-center mb-4"
      >
        <CheckCircle2 className="h-8 w-8 text-green-600" />
      </motion.div>
      <div className="text-2xl font-extrabold text-slate-900 mb-1.5 tracking-tight">
        Your quote is ready!
      </div>
      <div className="text-sm text-slate-500 max-w-sm mx-auto mb-5 leading-relaxed">
        We've sent your details to our team. They'll call you in <strong className="text-slate-900">{(lead.callback_eta || "30_minutes").replace("_", " ")}</strong> to confirm pickup. The price card behind shows your custom quote.
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5 text-left">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500 mb-1">
          Reference
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="font-mono font-extrabold text-slate-900 tracking-wider">{ref}</div>
          <button
            onClick={copy}
            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 hover:text-slate-900 transition-colors"
            aria-label="Copy reference"
          >
            {copied ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-200">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-500">Quoted</div>
            <div className="text-sm font-extrabold text-slate-900 mt-0.5 font-mono">
              ₹{(lead?.quoted_price ?? price?.amount_display ?? 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-500">Vehicle</div>
            <div className="text-sm font-bold text-slate-900 mt-0.5 truncate">
              {vehicle?.display || "—"}
            </div>
          </div>
        </div>
      </div>

      {lead.whatsapp_link && (
        <a
          href={lead.whatsapp_link}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-extrabold text-sm transition-all shadow-md hover:shadow-lg"
        >
          <MessageCircle className="h-4 w-4" />
          Chat on WhatsApp
        </a>
      )}
      <p className="text-[11px] text-slate-400 mt-4">
        Our team will call you shortly to confirm.
      </p>
    </div>
  );
};

export default CarServiceBookingModal;
